/**
 * Plays an expert's screen recording and says so when the browser cannot.
 *
 * `load` fetches a short-lived download link (one hour by default). An expired link fails like a file the
 * browser cannot decode, so a playback error fetches a fresh link and resumes where the viewer
 * was. A link cannot expire again within minutes of being fetched, so an error that soon after a
 * refresh is the file or the connection: the player then says so, offers the download and a
 * retry. A video whose sound plays without a picture (e.g. HEVC where the platform cannot
 * decode it) gets a softer note. A fixed `src` (the introduction example) is played as is.
 * Key the player by file so another recording starts afresh.
 */
import { useEffect, useRef, useState, type SyntheticEvent } from 'react'

/** Shorter than the link lifetime: a second error within this window is not an expired link. */
const REFRESH_COOLDOWN_MS = 5 * 60 * 1000

type Source = { src: string; load?: never } | { load: () => Promise<string>; src?: never }
type Props = Source & { onDownload?: () => void; active?: boolean }
type Problem = 'unplayable' | 'connection' | 'no-picture'

export function RecordingPlayer({ src, load, onDownload, active = true }: Props) {
  const [source, setSource] = useState(src ?? '')
  const [linkError, setLinkError] = useState('')
  const [problem, setProblem] = useState<Problem | null>(null)
  const [attempt, setAttempt] = useState(0)
  const [mount, setMount] = useState(0)
  const loader = useRef(load)
  loader.current = load
  const player = useRef<HTMLVideoElement>(null)
  const shown = useRef(active)
  shown.current = active
  const refreshedAt = useRef(0)
  const position = useRef(0)
  const playing = useRef(false)
  const resume = useRef<{ at: number; play: boolean } | null>(null)

  useEffect(() => {
    const fetchLink = loader.current
    if (!fetchLink) return
    let cancelled = false
    setLinkError('')
    fetchLink().then(
      (url) => { if (!cancelled) setSource(url) },
      (err: Error) => { if (!cancelled) setLinkError(err.message) },
    )
    return () => { cancelled = true }
  }, [attempt])
  useEffect(() => {
    if (!active) player.current?.pause()
  }, [active])

  /** Mount a new player (on a fresh link when there is one), resuming at the current position. */
  function reload() {
    refreshedAt.current = Date.now()
    // A replacement that failed before loading never reached the saved place; keep it.
    resume.current ??= { at: position.current, play: playing.current }
    setProblem(null)
    setMount((value) => value + 1)  // a new element, even if the new link is identical
    if (load) {
      setSource('')
      setAttempt((value) => value + 1)
    }
  }
  function failed(event: SyntheticEvent<HTMLVideoElement>) {
    // During a seek that failed, currentTime already reports the seek target. A replacement that
    // fails before loading reports 0, so the pending resume position is kept instead.
    if (!resume.current) position.current = event.currentTarget.currentTime
    if (load && Date.now() - refreshedAt.current > REFRESH_COOLDOWN_MS) return reload()
    setProblem(event.currentTarget.error?.code === MediaError.MEDIA_ERR_NETWORK ? 'connection' : 'unplayable')
  }
  function loaded(event: SyntheticEvent<HTMLVideoElement>) {
    const video = event.currentTarget
    if (!video.videoWidth && !video.videoHeight) setProblem('no-picture')
    const pending = resume.current
    resume.current = null
    if (pending?.at) video.currentTime = pending.at
    playing.current = false  // the failed element unmounted without a pause event; onPlay sets it again
    // Not while the viewer is on another tab or section; the player was paused for that.
    if (pending?.play && shown.current) void video.play().catch(() => {})
  }
  function resized(event: SyntheticEvent<HTMLVideoElement>) {
    if (event.currentTarget.videoWidth) setProblem((current) => (current === 'no-picture' ? null : current))
  }

  const retry = <button className="link small" onClick={reload}>Try again</button>
  return (
    <>
      {linkError ? (
        <p className="note err" role="alert">
          {linkError}{' '}
          <button className="link small" onClick={() => setAttempt((value) => value + 1)}>Try again</button>
        </p>
      ) : !source ? (
        <p className="muted small" role="status">Loading recording…</p>
      ) : problem !== 'unplayable' && problem !== 'connection' && (
        <video key={mount} ref={player} className="review-video" controls src={source} preload="metadata"
          onError={failed} onLoadedMetadata={loaded} onResize={resized}
          onTimeUpdate={(event) => { position.current = event.currentTarget.currentTime }}
          onPlay={() => { playing.current = true }} onPause={() => { playing.current = false }} />
      )}
      {problem === 'unplayable' && (
        <p className="note err" role="alert">
          This recording can’t play in your browser.{' '}
          {onDownload && <button className="link small" onClick={onDownload}>Download it to watch</button>}{' '}
          {retry}
        </p>
      )}
      {problem === 'connection' && (
        <p className="note err" role="alert">
          The connection to the recording was lost. {retry}
        </p>
      )}
      {problem === 'no-picture' && (
        <p className="note" role="status">
          Only the sound of this recording plays in your browser.{' '}
          {onDownload && <button className="link small" onClick={onDownload}>Download it to see the picture</button>}
        </p>
      )}
    </>
  )
}
